import { useTranslation } from "react-i18next";
import { Ban } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { formatDateShort } from "../format-user";
import { AcceptanceSourceBadge } from "./AcceptanceSourceBadge";
import type { AcceptanceRow, OfflineAcceptanceRow } from "./acceptance-rows";

interface AcceptanceRowItemProps {
  readonly row: AcceptanceRow;
  readonly canManage: boolean;
  /** Resolved display names by user id; unresolved ids fall back to the id. */
  readonly names: ReadonlyMap<string, string>;
  /** `trigger` is the clicked button, so the caller can restore focus sensibly. */
  readonly onRevoke: (row: OfflineAcceptanceRow, trigger: HTMLElement) => void;
}

const dateOrDash = (iso: string): string => formatDateShort(iso) || "—";

/** One acceptance line: source badge, what was accepted, and the audit trail. */
export function AcceptanceRowItem({ row, canManage, names, onRevoke }: AcceptanceRowItemProps) {
  const { t } = useTranslation();

  if (row.kind === "in-app") {
    return (
      <li
        data-testid={`tos-row-in-app-${row.id}`}
        className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm"
      >
        <AcceptanceSourceBadge kind="in-app" />
        <div className="min-w-[12rem] flex-1">
          <p className="truncate font-medium">
            {t("users.tos.row.version", { version: row.title })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("users.tos.row.acceptedOn", { date: dateOrDash(row.at) })}
          </p>
        </div>
      </li>
    );
  }

  const nameOf = (id: string) => names.get(id) ?? id;
  const { details } = row;
  const isLive = row.revokedAt === null;

  return (
    <li
      data-testid={`tos-row-offline-${row.id}`}
      className="flex flex-wrap items-start gap-3 px-3 py-2 text-sm"
    >
      <AcceptanceSourceBadge kind="offline" />
      <div className="min-w-[12rem] flex-1 space-y-0.5">
        <p className="truncate font-medium">{row.title}</p>
        <p className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
          <span>{t("users.tos.row.recordedOn", { date: dateOrDash(row.at) })}</span>
          {details && (
            <span>{t("users.tos.row.recordedBy", { name: nameOf(details.recordedByUserId) })}</span>
          )}
        </p>
        {details && (
          <>
            <p className="break-words text-xs text-muted-foreground">
              {t("users.tos.row.reference", { value: details.reference })}
            </p>
            {details.note && (
              <p className="break-words text-xs text-muted-foreground">
                {t("users.tos.row.note", { value: details.note })}
              </p>
            )}
          </>
        )}
        {!isLive && (
          <p className="flex flex-wrap gap-x-3 break-words text-xs text-muted-foreground">
            <span>{t("users.tos.row.revokedOn", { date: dateOrDash(row.revokedAt ?? "") })}</span>
            {details?.revokedByUserId && (
              <span>{t("users.tos.row.revokedBy", { name: nameOf(details.revokedByUserId) })}</span>
            )}
            {details?.revokedReason && (
              <span className="break-words">
                {t("users.tos.row.revokedReason", { value: details.revokedReason })}
              </span>
            )}
          </p>
        )}
      </div>
      <Badge variant={isLive ? "success" : "secondary"}>
        {isLive ? t("users.tos.status.live") : t("users.tos.status.revoked")}
      </Badge>
      {canManage && isLive && (
        <Button
          data-testid={`tos-revoke-${row.id}`}
          variant="outline"
          size="sm"
          aria-label={t("users.tos.revokeFor", { documentType: row.title })}
          onClick={(event) => onRevoke(row, event.currentTarget)}
        >
          <Ban className="mr-2 h-4 w-4" aria-hidden="true" />
          {t("users.tos.revoke")}
        </Button>
      )}
    </li>
  );
}
